import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Plus,
    Trash2,
    Pencil,
    X,
    FileText,
    AlertCircle
} from 'lucide-react';
import { API } from '../../config';

const AdminSiteContent = () => {
    const [contents, setContents] = useState([]);
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingContent, setEditingContent] = useState(null);
    const [loading, setLoading] = useState(false);
    const [formData, setFormData] = useState({
        section_name: '',
        title: '',
        content: ''
    });

    useEffect(() => {
        fetchContents();
    }, []);

    const fetchContents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${API}/site-content`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setContents(response.data);
        } catch (error) {
            toast.error('Failed to fetch site content');
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            if (editingContent) {
                await axios.put(`${API}/site-content/${editingContent.id}`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Content updated!');
            } else {
                await axios.post(`${API}/site-content`, formData, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                toast.success('Content added!');
            }
            resetForm();
            fetchContents();
        } catch (error) {
            toast.error('Failed to save site content');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setShowAddForm(false);
        setEditingContent(null);
        setFormData({ section_name: '', title: '', content: '' });
    };

    const handleEdit = (content) => {
        setEditingContent(content);
        setFormData({
            section_name: content.section_name,
            title: content.title,
            content: content.content
        });
        setShowAddForm(true);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Delete this content section?')) return;
        try {
            const token = localStorage.getItem('token');
            await axios.delete(`${API}/site-content/${id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Content deleted!');
            fetchContents();
        } catch (error) {
            toast.error('Failed to delete content');
        }
    };

    const sectionOptions = [
        { id: 'home_intro', label: 'Home Introduction' },
        { id: 'vision', label: 'Our Vision' },
        { id: 'mission', label: 'Our Mission' },
        { id: 'impact', label: 'Our Impact' },
        { id: 'footer_about', label: 'Footer About' }
    ];

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="bg-white rounded-3xl shadow-sm p-8"
        >
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h2 className="text-2xl font-bold text-gray-900">Site Content Blocks</h2>
                    <p className="text-gray-500 text-sm mt-1">Manage static text blocks used across the home page and footer</p>
                </div>
                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => { resetForm(); setShowAddForm(true); }}
                    className="bg-gray-900 text-white px-5 py-2.5 rounded-xl hover:bg-black transition-all shadow-sm flex items-center font-medium text-sm"
                >
                    <Plus className="h-4 w-4 mr-2" />
                    Add Content Block
                </motion.button>
            </div>

            {/* Form */}
            <AnimatePresence>
                {showAddForm && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="overflow-hidden mb-8"
                    >
                        <div className="bg-gray-50 rounded-3xl p-8 border border-gray-200">
                            <div className="flex justify-between items-center mb-6">
                                <h3 className="text-lg font-bold text-gray-900">
                                    {editingContent ? 'Edit Content Block' : 'Add New Content Block'}
                                </h3>
                                <button onClick={resetForm} className="text-gray-400 hover:text-gray-600">
                                    <X className="h-5 w-5" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-6">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Section Identifier (internal)</label>
                                        <select
                                            required
                                            value={formData.section_name}
                                            onChange={(e) => setFormData({ ...formData, section_name: e.target.value })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                        >
                                            <option value="">Select a section...</option>
                                            {sectionOptions.map(opt => (
                                                <option key={opt.id} value={opt.id}>{opt.label}</option>
                                            ))}
                                            <option value="custom">Custom ID (Advanced)</option>
                                        </select>
                                        {formData.section_name === 'custom' && (
                                            <input
                                                type="text"
                                                placeholder="Enter custom ID"
                                                onChange={(e) => setFormData({ ...formData, section_name: e.target.value })}
                                                className="mt-2 w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900 text-sm"
                                            />
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">Display Title</label>
                                        <input
                                            type="text"
                                            required
                                            value={formData.title}
                                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                            className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900"
                                            placeholder="e.g. Pioneering Ethical Broiler Farming"
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Body Content (HTML or Markdown like bullets supported)</label>
                                    <textarea
                                        rows="10"
                                        required
                                        value={formData.content}
                                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                                        className="w-full px-4 py-2 rounded-xl border border-gray-300 outline-none focus:ring-2 focus:ring-gray-900 font-serif"
                                        placeholder="Enter the main body text..."
                                    />
                                </div>
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-gray-900 text-white px-8 py-2.5 rounded-xl font-medium disabled:opacity-50"
                                    >
                                        {loading ? 'Saving...' : 'Save Content Block'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={resetForm}
                                        className="px-8 py-2.5 rounded-xl border border-gray-300 font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* List */}
            <div className="space-y-4">
                {contents.map((content) => (
                    <div key={content.id} className="p-6 bg-gray-50 rounded-2xl border border-gray-100 group">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <span className="bg-gray-200 text-gray-700 text-[10px] uppercase tracking-widest font-bold px-2 py-1 rounded">
                                    {content.section_name}
                                </span>
                                <h3 className="text-xl font-bold text-gray-900 mt-2">{content.title}</h3>
                            </div>
                            <div className="flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <button onClick={() => handleEdit(content)} className="p-2 text-gray-400 hover:text-gray-900 bg-white rounded-lg shadow-sm border border-gray-100">
                                    <Pencil className="h-4 w-4" />
                                </button>
                                <button onClick={() => handleDelete(content.id)} className="p-2 text-gray-400 hover:text-red-500 bg-white rounded-lg shadow-sm border border-gray-100">
                                    <Trash2 className="h-4 w-4" />
                                </button>
                            </div>
                        </div>
                        <p className="text-gray-600 text-sm whitespace-pre-line line-clamp-3 font-sans leading-relaxed">
                            {content.content}
                        </p>
                    </div>
                ))}
                {contents.length === 0 && (
                    <div className="py-16 text-center text-gray-400 flex flex-col items-center">
                        <FileText className="h-12 w-12 mb-3 opacity-20" />
                        <p>No content blocks configured yet</p>
                    </div>
                )}
            </div>

            <div className="mt-8 p-4 bg-blue-50 border border-blue-100 rounded-2xl flex items-start">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-3 flex-shrink-0" />
                <p className="text-sm text-blue-700 leading-relaxed font-sans">
                    <strong>Note:</strong> Section identifiers like <code>home_intro</code>, <code>vision</code>, <code>mission</code>, and <code>impact</code> are used by the homepage to know where to display the text. Changing these might hide content from the site.
                </p>
            </div>
        </motion.div>
    );
};

export default AdminSiteContent;
